import BaseService from './BaseService';
import {
  CatalogResponse,
  CredentialsResponse,
  KittingActionsResponse,
  KittingRule,
  OrderDetailResponse,
  OrderKittingSettings,
  PreviewResponse,
} from 'pages/OrderKitting/types';

// Client for the Order Kitting endpoints (sbly-dash-collector
// src/sbly-api-server/orderKitting). Rule/mode edits go through the generic
// SettingsService with the ORDER_KITTING_NAMESPACE — the backend job applies
// defaults and clamps when it consumes them.
class OrderKittingService extends BaseService {
  basePath = 'order-kitting';

  async getActions(params: {
    startDate: string;
    endDate: string;
    mode?: string;
    outcome?: string;
    store?: string;
    limit?: number;
    offset?: number;
  }) {
    await this._getAccessToken();

    return this.handleHttpRequest<KittingActionsResponse>(
      () => this.http.get(`${this.basePath}/actions`, { params }),
      'Failed to fetch order-kitting actions',
    );
  }

  // A store's variant catalog (SKU, names, price, inventory) for the rule
  // editor's dropdowns. Served live from Shopify, cached ~5 min in the backend.
  async getCatalog(store: string) {
    await this._getAccessToken();

    return this.handleHttpRequest<CatalogResponse>(
      () => this.http.get(`${this.basePath}/catalog`, { params: { store } }),
      'Failed to fetch order-kitting catalog',
    );
  }

  // Every line item on one order — the log only records the lines a rule
  // consumed, so the rest of the order comes from Shopify live.
  async getOrderDetail(store: string, orderId: number) {
    await this._getAccessToken();

    return this.handleHttpRequest<OrderDetailResponse>(
      () => this.http.get(`${this.basePath}/order-detail`, { params: { store, orderId } }),
      'Failed to fetch order detail',
    );
  }

  // Per-store token health (never returns a token value): whether each store
  // can be evaluated at all, and whether it could actually edit orders.
  async getCredentials() {
    await this._getAccessToken();

    return this.handleHttpRequest<CredentialsResponse>(
      () => this.http.get(`${this.basePath}/credentials`),
      'Failed to fetch order-kitting credentials',
    );
  }

  // What would this rule do to real orders? Read-only on the backend (runs the
  // job's own matcher, logs nothing); POST only because the rule is a body, so
  // an unsaved draft can be previewed too.
  async previewRule(store: string, rule: Partial<KittingRule>) {
    await this._getAccessToken();

    return this.handleHttpRequest<PreviewResponse>(
      () => this.http.post(`${this.basePath}/preview`, { store, rule }),
      'Failed to preview rule',
    );
  }

  // Effective settings: stored values with the backend's defaults/clamps applied.
  async getEffectiveSettings() {
    await this._getAccessToken();

    return this.handleHttpRequest<OrderKittingSettings>(
      () => this.http.get(`${this.basePath}/settings`),
      'Failed to fetch order-kitting settings',
    );
  }
}

export default OrderKittingService;
